import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  Loader2,
  Check,
  X,
  CheckCircle2,
  XCircle,
  Download,
  Trash2,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertOctagon,
  HelpCircle,
  Camera,
  Volume2,
  Upload,
  Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InspectionPage = () => {
  const { id } = useParams();
  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [notes, setNotes] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInspection();
  }, [id]);

  const fetchInspection = async () => {
    try {
      const response = await axios.get(`${API}/inspections/${id}`, {
        headers: getAuthHeaders()
      });
      setInspection(response.data);
      setNotes(response.data.notes || "");
      
      // Auto-expand all categories
      const categories = {};
      response.data.answers?.forEach(a => {
        categories[a.category] = true;
      });
      setExpandedCategories(categories);
    } catch (error) {
      toast.error("Kon keuring niet laden");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const toggleAnswer = (questionId, passed) => {
    if (inspection.status !== "in_progress") return;
    
    setInspection(prev => ({
      ...prev,
      answers: prev.answers.map(a => {
        if (a.question_id === questionId) {
          const newPassed = a.passed === passed ? null : passed;
          return { 
            ...a, 
            passed: newPassed,
            // Reset severity when passing or clearing
            severity: newPassed === false ? (a.severity || "dringend") : null
          };
        }
        return a;
      })
    }));
  };

  const updateSeverity = (questionId, severity) => {
    if (inspection.status !== "in_progress") return;
    
    setInspection(prev => ({
      ...prev,
      answers: prev.answers.map(a => 
        a.question_id === questionId 
          ? { ...a, severity }
          : a
      )
    }));
  };

  const updateNotes = (questionId, newNotes) => {
    setInspection(prev => ({
      ...prev,
      answers: prev.answers.map(a => 
        a.question_id === questionId 
          ? { ...a, notes: newNotes }
          : a
      )
    }));
  };

  const updateDecibelValue = (questionId, value) => {
    setInspection(prev => ({
      ...prev,
      answers: prev.answers.map(a => 
        a.question_id === questionId 
          ? { ...a, decibel_value: value ? parseFloat(value) : null }
          : a
      )
    }));
  };

  const togglePendingReview = (questionId) => {
    if (inspection.status !== "in_progress") return;
    
    setInspection(prev => ({
      ...prev,
      answers: prev.answers.map(a => {
        if (a.question_id === questionId) {
          const newPendingReview = !a.pending_review;
          return { 
            ...a, 
            pending_review: newPendingReview,
            // Clear passed status if marking for review
            passed: newPendingReview ? null : a.passed,
            severity: newPendingReview ? null : a.severity
          };
        }
        return a;
      })
    }));
  };

  const handlePhotoUpload = async (questionId, file) => {
    if (!file) return;
    
    setUploadingPhoto(questionId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/inspections/${id}/upload/${questionId}`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      // Update local state with new photo
      setInspection(prev => ({
        ...prev,
        answers: prev.answers.map(a => 
          a.question_id === questionId 
            ? { ...a, photos: [...(a.photos || []), response.data.filename] }
            : a
        )
      }));
      
      toast.success("Foto geüpload!");
    } catch (error) {
      toast.error("Kon foto niet uploaden");
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleDeletePhoto = async (questionId, filename) => {
    try {
      await axios.delete(
        `${API}/inspections/${id}/photo/${questionId}/${filename}`,
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setInspection(prev => ({
        ...prev,
        answers: prev.answers.map(a => 
          a.question_id === questionId 
            ? { ...a, photos: (a.photos || []).filter(p => p !== filename) }
            : a
        )
      }));
      
      toast.success("Foto verwijderd");
    } catch (error) {
      toast.error("Kon foto niet verwijderen");
    }
  };

  const saveInspection = async (newStatus = null) => {
    setSaving(true);
    try {
      const status = newStatus || inspection.status;
      await axios.put(`${API}/inspections/${id}`, {
        answers: inspection.answers,
        status,
        notes
      }, {
        headers: getAuthHeaders()
      });
      
      setInspection(prev => ({ ...prev, status }));
      toast.success(
        newStatus === "approved" ? "Keuring goedgekeurd!" :
        newStatus === "rejected" ? "Keuring afgekeurd!" :
        newStatus === "pending_review" ? "Keuring ter beoordeling ingediend!" :
        "Keuring opgeslagen!"
      );
      
      if (newStatus) {
        navigate("/");
      }
    } catch (error) {
      toast.error("Kon keuring niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const deleteInspection = async () => {
    try {
      await axios.delete(`${API}/inspections/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success("Keuring verwijderd");
      navigate("/");
    } catch (error) {
      toast.error("Kon keuring niet verwijderen");
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/inspections/${id}/pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `keuring_${inspection.vehicle?.kenteken || 'rapport'}_${new Date().toISOString().slice(0,10)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success("PDF gedownload!");
    } catch (error) {
      console.error("PDF download error:", error);
      toast.error("Kon PDF niet downloaden");
    } finally {
      setDownloading(false);
    }
  };

  const formatKenteken = (kenteken) => {
    if (!kenteken) return "-";
    return kenteken.replace(/-/g, "").toUpperCase();
  };

  const getCountryBadge = (land) => {
    const countries = {
      NL: { code: "NL", bg: "bg-blue-700", text: "text-white" },
      BE: { code: "BE", bg: "bg-black", text: "text-yellow-400" },
      DE: { code: "DE", bg: "bg-black", text: "text-yellow-400" },
      FR: { code: "FR", bg: "bg-blue-600", text: "text-white" },
      GB: { code: "GB", bg: "bg-blue-900", text: "text-white" },
      OTHER: { code: "INT", bg: "bg-gray-600", text: "text-white" }
    };
    return countries[land] || countries["NL"];
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group answers by category
  const groupedAnswers = inspection?.answers?.reduce((acc, answer) => {
    if (!acc[answer.category]) {
      acc[answer.category] = [];
    }
    acc[answer.category].push(answer);
    return acc;
  }, {}) || {};

  // Items marked for pending review
  const pendingReviewItems = inspection?.answers?.filter(a => a.pending_review) || [];
  const hasPendingReview = pendingReviewItems.length > 0;
  
  // All items that need to be answered (not pending review)
  const answeredItems = inspection?.answers?.filter(a => !a.pending_review) || [];
  const allAnswered = answeredItems.every(a => a.passed !== null);
  const someFailed = answeredItems.some(a => a.passed === false);
  
  // Check if all failed items have severity set
  const failedItems = answeredItems.filter(a => a.passed === false) || [];
  const allFailedHaveSeverity = failedItems.every(a => a.severity);

  // Check if all decibel questions have a value filled in
  const decibelItems = inspection?.answers?.filter(a => a.question_type === "decibel" && !a.pending_review) || [];
  const allDecibelFilled = decibelItems.every(a => a.decibel_value != null && a.decibel_value !== "");

  // Count severities
  const dwingendeCount = failedItems.filter(a => a.severity === "dwingend").length;
  const dringendeCount = failedItems.filter(a => a.severity === "dringend").length;

  // Goedkeuren is mogelijk als:
  // - Alle items beantwoord (behalve pending review)
  // - Geen dwingend punten EN
  // - Maximaal 3 dringend punten
  // - Geen items ter beoordeling
  // - Alle dB-metingen ingevuld
  const canApprove = allAnswered && allFailedHaveSeverity && allDecibelFilled && dwingendeCount === 0 && dringendeCount <= 3 && !hasPendingReview;
  
  // Moet afgekeurd worden als:
  // - Er dwingend punten zijn OF meer dan 3 dringend punten
  const mustReject = dwingendeCount > 0 || dringendeCount > 3;

  // Kan ter beoordeling als er items zijn gemarkeerd voor review
  const canSubmitForReview = allAnswered && allFailedHaveSeverity && allDecibelFilled && hasPendingReview;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                className="text-white hover:bg-white/10"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading font-black text-xl">Keuring</h1>
                <p className="text-white/70 text-sm">
                  {inspection.vehicle?.merk} {inspection.vehicle?.handelsbenaming}
                </p>
              </div>
            </div>
            {(() => {
              const land = inspection.vehicle?.land || "NL";
              const countryStyle = getCountryBadge(land);
              return (
                <div className="flex items-stretch border-2 border-black rounded overflow-hidden">
                  <div className={`${countryStyle.bg} ${countryStyle.text} px-2 flex items-center text-xs font-bold`}>
                    {countryStyle.code}
                  </div>
                  <div className={`px-3 py-1 flex items-center ${
                    land === "NL" ? 'bg-license-yellow' : 'bg-white'
                  }`}>
                    <span className="font-mono font-bold text-sm tracking-wider text-black">
                      {formatKenteken(inspection.vehicle?.kenteken)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 pb-48">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-6">
          <StatusBadge status={inspection.status} />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloading}
              data-testid="download-pdf-button"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive/10"
              onClick={() => setShowDeleteDialog(true)}
              data-testid="delete-button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Severity Summary (when rejected) */}
        {inspection.status === "rejected" && (dwingendeCount > 0 || dringendeCount > 0) && (
          <Card className="mb-6 border-2 border-red-200 bg-red-50" data-testid="severity-summary">
            <CardContent className="p-4">
              <h3 className="font-heading font-bold text-lg mb-3 text-red-800">Afkeur Samenvatting</h3>
              <div className="flex gap-4">
                {dwingendeCount > 0 && (
                  <div className="flex items-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg">
                    <AlertOctagon className="w-5 h-5" />
                    <span className="font-bold">{dwingendeCount} Dwingend</span>
                  </div>
                )}
                {dringendeCount > 0 && (
                  <div className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-bold">{dringendeCount} Dringend</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicle Info */}
        <Card className="mb-6" data-testid="vehicle-info-card">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Kleur</p>
                <p className="font-semibold">{inspection.vehicle?.kleur || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Brandstof</p>
                <p className="font-semibold">{inspection.vehicle?.brandstof || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Eerste toelating</p>
                <p className="font-semibold">{inspection.vehicle?.eerste_toelating || "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">APK vervalt</p>
                <p className="font-semibold text-yellow-600">{inspection.vehicle?.vervaldatum_apk || "-"}</p>
              </div>
            </div>
            
            {/* Keurmeester en Beoordelaar info */}
            <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Keurmeester</p>
                <p className="font-semibold">{inspection.inspector_name || "-"}</p>
                <p className="text-xs text-muted-foreground">
                  {inspection.created_at ? new Date(inspection.created_at).toLocaleDateString('nl-NL') : "-"}
                </p>
              </div>
              {inspection.reviewer_name && (
                <div>
                  <p className="text-muted-foreground">Beoordeeld door</p>
                  <p className="font-semibold text-blue-600">{inspection.reviewer_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {inspection.reviewed_at ? new Date(inspection.reviewed_at).toLocaleDateString('nl-NL') : "-"}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Checklist */}
        <div className="space-y-4" data-testid="inspection-checklist">
          {Object.entries(groupedAnswers).map(([category, answers], catIndex) => {
            const categoryPassed = answers.filter(a => a.passed === true).length;
            const categoryFailed = answers.filter(a => a.passed === false).length;
            const categoryTotal = answers.length;
            const allPassed = categoryPassed === categoryTotal;
            const isExpanded = expandedCategories[category];
            
            // Function to approve all in category
            const approveAllInCategory = (e) => {
              e.stopPropagation();
              setInspection(prev => ({
                ...prev,
                answers: prev.answers.map(a => 
                  a.category === category && a.passed !== true && !a.pending_review
                    ? { ...a, passed: true, severity: null }
                    : a
                )
              }));
            };
            
            return (
              <Card key={category} className="overflow-hidden">
                <button
                  className="w-full p-3 sm:p-4 hover:bg-muted/50 transition-colors"
                  onClick={() => toggleCategory(category)}
                  data-testid={`category-${catIndex}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-heading font-bold text-sm sm:text-lg text-left leading-tight flex-1 min-w-0">{category}</h3>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Badge 
                        variant="outline" 
                        className={`font-mono text-xs ${allPassed ? 'bg-green-100 text-green-700 border-green-300' : ''}`}
                      >
                        {categoryPassed}/{categoryTotal}
                      </Badge>
                      {categoryFailed > 0 && (
                        <Badge variant="outline" className="font-mono text-xs bg-red-100 text-red-700 border-red-300">
                          {categoryFailed} ✗
                        </Badge>
                      )}
                      {/* Approve All Button inline */}
                      {inspection.status === "in_progress" && !allPassed && (
                        <span
                          className="p-1.5 text-green-700 bg-green-100 hover:bg-green-200 rounded-md transition-colors inline-flex items-center gap-1"
                          onClick={(e) => { e.stopPropagation(); approveAllInCategory(e); }}
                          data-testid={`approve-all-${catIndex}`}
                          title="Alle items in deze categorie goedkeuren"
                          role="button"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="hidden sm:inline text-xs font-medium">Alles OK</span>
                        </span>
                      )}
                      {allPassed && (
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </button>
                
                {isExpanded && (
                  <div className="border-t divide-y">
                    {answers.map((answer, idx) => {
                      const isReadOnly = inspection.status !== "in_progress";
                      
                      return (
                      <div 
                        key={answer.question_id} 
                        className={`p-3 sm:p-4 ${
                          answer.pending_review ? 'bg-blue-50' :
                          answer.passed === false ? 'bg-red-50' : ''
                        }`}
                        data-testid={`question-${catIndex}-${idx}`}
                      >
                        {/* Read-only compact view */}
                        {isReadOnly ? (
                          <div className="flex items-start gap-2.5">
                            {/* Compact status icon */}
                            <div className="flex-shrink-0 mt-0.5">
                              {answer.pending_review ? (
                                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center">
                                  <HelpCircle className="w-3.5 h-3.5" />
                                </div>
                              ) : answer.passed === true ? (
                                <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center">
                                  <Check className="w-3.5 h-3.5" />
                                </div>
                              ) : answer.passed === false ? (
                                <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center">
                                  <X className="w-3.5 h-3.5" />
                                </div>
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 text-white flex items-center justify-center">
                                  <span className="text-xs font-bold">-</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Question text + inline details */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm leading-snug text-foreground">
                                {answer.question_text}
                              </p>
                              {/* Inline badges & notes */}
                              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                {answer.passed === false && answer.severity && (
                                  <Badge className={`text-[10px] px-1.5 py-0 h-5 ${
                                    answer.severity === "dwingend" 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-orange-500 text-white'
                                  }`}>
                                    {answer.severity === "dwingend" ? (
                                      <><AlertOctagon className="w-2.5 h-2.5 mr-0.5" />Dwingend</>
                                    ) : (
                                      <><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Dringend</>
                                    )}
                                  </Badge>
                                )}
                                {answer.question_type === "decibel" && answer.decibel_value != null && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                                    <Volume2 className="w-2.5 h-2.5 mr-0.5" />{answer.decibel_value} dB
                                  </Badge>
                                )}
                                {answer.question_type === "photo" && (answer.photos || []).length > 0 && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5">
                                    <Camera className="w-2.5 h-2.5 mr-0.5" />{answer.photos.length} foto
                                  </Badge>
                                )}
                                {answer.notes && (
                                  <span className="text-xs text-muted-foreground italic">{answer.notes}</span>
                                )}
                              </div>
                              {/* Show photos inline for read-only */}
                              {answer.question_type === "photo" && (answer.photos || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {answer.photos.map((photo, photoIdx) => (
                                    <img 
                                      key={photoIdx}
                                      src={`${BACKEND_URL}/api/uploads/${photo}`}
                                      alt={`Foto ${photoIdx + 1}`}
                                      className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                                      onClick={() => setLightboxPhoto(`${BACKEND_URL}/api/uploads/${photo}`)}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                        /* Interactive edit view */
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                          {/* Row 1 on mobile: Checkboxes + Question text inline */}
                          <div className="flex items-start gap-3">
                            {/* Toggle Buttons */}
                            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                              <button
                                className={`inspection-checkbox-sm sm:inspection-checkbox ${answer.passed === true ? 'passed' : 'pending'}`}
                                onClick={() => toggleAnswer(answer.question_id, true)}
                                disabled={answer.pending_review}
                                data-testid={`check-pass-${catIndex}-${idx}`}
                              >
                                <Check className="w-5 h-5 sm:w-6 sm:h-6" />
                              </button>
                              <button
                                className={`inspection-checkbox-sm sm:inspection-checkbox ${answer.passed === false ? 'failed' : 'pending'}`}
                                onClick={() => toggleAnswer(answer.question_id, false)}
                                disabled={answer.pending_review}
                                data-testid={`check-fail-${catIndex}-${idx}`}
                              >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                              </button>
                              {/* Pending Review Button */}
                              <button
                                className={`inspection-checkbox-sm sm:inspection-checkbox ${answer.pending_review ? 'review' : 'pending'}`}
                                onClick={() => togglePendingReview(answer.question_id)}
                                title="Ter beoordeling organisatie"
                                data-testid={`check-review-${catIndex}-${idx}`}
                              >
                                <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                              </button>
                            </div>
                            
                            {/* Question text */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-foreground text-sm sm:text-base">
                                  {answer.question_text}
                                </p>
                                {answer.question_type === "decibel" && (
                                  <Badge variant="outline" className="text-xs">
                                    <Volume2 className="w-3 h-3 mr-1" />dB
                                  </Badge>
                                )}
                                {answer.question_type === "photo" && (
                                  <Badge variant="outline" className="text-xs">
                                    <Camera className="w-3 h-3 mr-1" />Foto
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Content below on mobile, inline on desktop */}
                          <div className="flex-1 sm:flex-none sm:w-auto w-full">
                            {/* Pending Review badge */}
                            {answer.pending_review && (
                              <div className="mb-2">
                                <Badge className="bg-blue-500 text-white">
                                  <HelpCircle className="w-3 h-3 mr-1" /> Ter Beoordeling
                                </Badge>
                              </div>
                            )}
                            
                            {/* Decibel Input */}
                            {answer.question_type === "decibel" && !answer.pending_review && (
                              <div className="mb-2 flex items-center gap-2" data-testid={`decibel-input-${catIndex}-${idx}`}>
                                <Volume2 className="w-5 h-5 text-gray-500" />
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="200"
                                  placeholder="dB waarde"
                                  value={answer.decibel_value || ""}
                                  onChange={(e) => updateDecibelValue(answer.question_id, e.target.value)}
                                  className="w-24 sm:w-32"
                                />
                                <span className="text-sm text-gray-600 font-medium">dB</span>
                              </div>
                            )}

                            {/* Photo Upload */}
                            {answer.question_type === "photo" && !answer.pending_review && (
                              <div className="mb-2" data-testid={`photo-upload-${catIndex}-${idx}`}>
                                <div className="flex flex-wrap gap-2 mb-2">
                                  {(answer.photos || []).map((photo, photoIdx) => (
                                    <div key={photoIdx} className="relative group">
                                      <img 
                                        src={`${BACKEND_URL}/api/uploads/${photo}`}
                                        alt={`Foto ${photoIdx + 1}`}
                                        className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => setLightboxPhoto(`${BACKEND_URL}/api/uploads/${photo}`)}
                                      />
                                      <button
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() => handleDeletePhoto(answer.question_id, photo)}
                                        title="Verwijder foto"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <label className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors text-sm">
                                  {uploadingPhoto === answer.question_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Upload className="w-4 h-4" />
                                  )}
                                  <span className="font-medium">Foto</span>
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    className="hidden"
                                    onChange={(e) => handlePhotoUpload(answer.question_id, e.target.files[0])}
                                    disabled={uploadingPhoto === answer.question_id}
                                  />
                                </label>
                              </div>
                            )}
                            
                            {/* Severity Selection */}
                            {answer.passed === false && !answer.pending_review && (
                              <div className="mb-2 grid grid-cols-2 gap-2" data-testid={`severity-${catIndex}-${idx}`}>
                                <button
                                  className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all ${
                                    answer.severity === "dwingend" 
                                      ? 'bg-red-500 text-white shadow-lg ring-2 ring-red-300' 
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  }`}
                                  onClick={() => updateSeverity(answer.question_id, "dwingend")}
                                  data-testid={`severity-dwingend-${catIndex}-${idx}`}
                                >
                                  <AlertOctagon className="w-4 h-4 flex-shrink-0" />
                                  <span>Dwingend</span>
                                </button>
                                <button
                                  className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg font-bold text-xs sm:text-sm transition-all ${
                                    answer.severity === "dringend" 
                                      ? 'bg-orange-500 text-white shadow-lg ring-2 ring-orange-300' 
                                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                  }`}
                                  onClick={() => updateSeverity(answer.question_id, "dringend")}
                                  data-testid={`severity-dringend-${catIndex}-${idx}`}
                                >
                                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                  <span>Dringend</span>
                                </button>
                              </div>
                            )}
                            
                            {/* Notes field */}
                            {!answer.pending_review && (
                              <input
                                type="text"
                                placeholder="Opmerkingen..."
                                value={answer.notes || ""}
                                onChange={(e) => updateNotes(answer.question_id, e.target.value)}
                                className="w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-2 placeholder:text-muted-foreground/50 focus:ring-2 focus:ring-primary focus:border-transparent"
                                data-testid={`notes-${catIndex}-${idx}`}
                              />
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* General Notes */}
        <Card className="mt-6" data-testid="general-notes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Algemene opmerkingen</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Voeg eventuele opmerkingen toe..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={inspection.status !== "in_progress"}
              rows={3}
              data-testid="general-notes-textarea"
            />
          </CardContent>
        </Card>
      </main>

      {/* Bottom Action Bar - For inspectors (in_progress) */}
      {inspection.status === "in_progress" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t safe-bottom z-50">
          <div className="max-w-2xl mx-auto p-4">
            <div className={`grid gap-3 ${hasPendingReview ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
              <Button
                variant="outline"
                className="h-14"
                onClick={() => saveInspection()}
                disabled={saving}
                data-testid="save-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Opslaan
              </Button>
              <Button
                className="h-14 bg-red-500 hover:bg-red-600 text-white font-bold active:scale-95 transition-all"
                onClick={() => saveInspection("rejected")}
                disabled={saving || !allAnswered || !allDecibelFilled || (someFailed && !allFailedHaveSeverity)}
                data-testid="reject-button"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Afkeuren
              </Button>
              {hasPendingReview && (
                <Button
                  className="h-14 bg-blue-500 hover:bg-blue-600 text-white font-bold active:scale-95 transition-all"
                  onClick={() => saveInspection("pending_review")}
                  disabled={saving || !canSubmitForReview}
                  data-testid="pending-review-button"
                >
                  <HelpCircle className="w-5 h-5 mr-2" />
                  <span className="hidden sm:inline">Ter </span>Beoordeling
                </Button>
              )}
              <Button
                className="h-14 bg-green-500 hover:bg-green-600 text-white font-bold active:scale-95 transition-all"
                onClick={() => saveInspection("approved")}
                disabled={saving || !allAnswered || !allFailedHaveSeverity || !canApprove}
                data-testid="approve-button"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Goedkeuren
              </Button>
            </div>
            {!allAnswered && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                Beantwoord alle vragen om de keuring af te ronden
              </p>
            )}
            {allAnswered && someFailed && !allFailedHaveSeverity && (
              <p className="text-center text-sm text-orange-600 mt-2 font-medium">
                Selecteer voor elk afkeurpunt: Dwingend of Dringend
              </p>
            )}
            {allAnswered && !allDecibelFilled && (
              <p className="text-center text-sm text-orange-600 mt-2 font-medium">
                Vul de dB(A) meting in om de keuring af te ronden
              </p>
            )}
            {allAnswered && allFailedHaveSeverity && hasPendingReview && (
              <p className="text-center text-sm text-blue-600 mt-2 font-medium">
                {pendingReviewItems.length} item(s) ter beoordeling - indienen bij organisatie
              </p>
            )}
            {allAnswered && allFailedHaveSeverity && !hasPendingReview && dringendeCount > 0 && dringendeCount <= 3 && dwingendeCount === 0 && (
              <p className="text-center text-sm text-green-600 mt-2 font-medium">
                Max 3 dringende adviezen - goedkeuren mogelijk
              </p>
            )}
            {allAnswered && allFailedHaveSeverity && !hasPendingReview && mustReject && (
              <p className="text-center text-sm text-red-600 mt-2 font-medium">
                {dwingendeCount > 0 ? "Dwingend advies aanwezig" : "Meer dan 3 dringende adviezen"} - afkeuren verplicht
              </p>
            )}
          </div>
        </div>
      )}

      {/* Bottom Action Bar - For organisation/admin role (pending_review status) */}
      {inspection.status === "pending_review" && (user?.role === "admin" || user?.role === "organisation") && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t safe-bottom z-50">
          <div className="max-w-2xl mx-auto p-4">
            <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium text-center">
                Deze keuring wacht op uw beoordeling. Bekijk de gemarkeerde items en beslis of het voertuig goedgekeurd of afgekeurd moet worden.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button
                className="h-14 bg-red-500 hover:bg-red-600 text-white font-bold active:scale-95 transition-all"
                onClick={() => saveInspection("rejected")}
                disabled={saving}
                data-testid="org-reject-button"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Afkeuren
              </Button>
              <Button
                className="h-14 bg-green-500 hover:bg-green-600 text-white font-bold active:scale-95 transition-all"
                onClick={() => saveInspection("approved")}
                disabled={saving}
                data-testid="org-approve-button"
              >
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Goedkeuren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Info message for medewerker viewing pending_review */}
      {inspection.status === "pending_review" && user?.role === "medewerker" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t safe-bottom z-50">
          <div className="max-w-2xl mx-auto p-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800 font-medium text-center">
                Deze keuring wacht op beoordeling door de organisatie. Je kunt de details bekijken maar niet wijzigen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Keuring verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze keuring wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={deleteInspection}
              data-testid="confirm-delete-button"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Photo Lightbox */}
      {lightboxPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxPhoto(null)}
          data-testid="photo-lightbox"
        >
          <button 
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-colors"
            onClick={() => setLightboxPhoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={lightboxPhoto} 
            alt="Foto vergroot"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

const StatusBadge = ({ status }) => {
  switch (status) {
    case "approved":
      return (
        <Badge className="status-approved px-4 py-2 text-base font-bold">
          <CheckCircle2 className="w-5 h-5 mr-2" />
          Goedgekeurd
        </Badge>
      );
    case "rejected":
      return (
        <Badge className="status-rejected px-4 py-2 text-base font-bold">
          <XCircle className="w-5 h-5 mr-2" />
          Afgekeurd
        </Badge>
      );
    case "pending_review":
      return (
        <Badge className="bg-blue-500 text-white px-4 py-2 text-base font-bold">
          <HelpCircle className="w-5 h-5 mr-2" />
          Ter Beoordeling
        </Badge>
      );
    default:
      return (
        <Badge className="status-pending px-4 py-2 text-base font-bold">
          <Clock className="w-5 h-5 mr-2" />
          In behandeling
        </Badge>
      );
  }
};

export default InspectionPage;
